import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserInfoContext,
  EmployeesContext,
  SubscriptionContext,
} from '../../context';

import PageTop from '../../components/page-top/page-top';
import PreLoader from '../../components/preloader/preloader';
import Tabs from '../../components/tabs/tabs';
import Button from '../../components/button/button';
import EmptyStateImage from './emptystate.webp';
import TimeRegistrationSettings from './timeregistration-settings';
import TimeRegistrationOverview from './timeregistration-overview';
import TimeRegistrationRegistrations from './timeregistration-registrations';
import TimeRegistrationUserRegistrationsModal from '../../components/timeregistration/user-registrations/timeregistration-user-registrations-modal';

import TimeRegistrationExceedings from './timeregistration-exceedings';
import { useLogs } from '../../logsContext';
import { convertToLocalTime } from '../../utils/formatDates';
import EmptyState from '../../components/empty-state/empty-state';

// Helpers for localStorage persistence with 24h expiry
const EXPIRY_HOURS = 24;
const now = () => new Date().getTime();
const getExpiry = () => now() + EXPIRY_HOURS * 60 * 60 * 1000;

const getInitialDateRange = () => {
  const stored = window.localStorage.getItem('timeregistration-dateRange');
  if (stored) {
    try {
      const obj = JSON.parse(stored);
      if (obj && obj.expires && obj.expires > now()) {
        return obj.value;
      }
    } catch {
      /* empty */
    }
  }
  const initialStartDate = new Date(
    Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0)
  );
  const initialEndDate = new Date(
    Date.UTC(
      initialStartDate.getFullYear(),
      initialStartDate.getMonth() + 1,
      1,
      23,
      59,
      59,
      999
    )
  );
  return {
    startDate: initialStartDate.toISOString().split('T')[0],
    endDate: initialEndDate.toISOString().split('T')[0],
  };
};

const getInitialPeriodType = () => {
  const stored = window.localStorage.getItem('timeregistration-periodType');
  if (stored) {
    try {
      const obj = JSON.parse(stored);
      if (obj && obj.expires && obj.expires > now()) {
        return obj.value;
      }
    } catch {
      /* empty */
    }
  }
  return 'Måned';
};

const getInitialEmployee = (employees) => {
  const stored = window.localStorage.getItem('timeregistration-employee');
  if (stored && employees && employees.length > 0) {
    try {
      const obj = JSON.parse(stored);
      if (obj && obj.expires && obj.expires > now()) {
        // Multi-select: obj.value is array of ids
        if (Array.isArray(obj.value)) {
          const foundEmployees = obj.value
            .map((id) => employees.find((e) => e.id === id))
            .filter(Boolean);
          return foundEmployees;
        } else {
          // Fallback for single select
          const found = employees.find((e) => e.id === obj.value);
          return found ? [found] : [];
        }
      }
    } catch {
      /* empty */
    }
  }
  return [];
};

const getInitialValueToDisplay = (dateRange, periodType) => {
  if (periodType === 'Uge') {
    // Calculate ISO week number
    const start = new Date(dateRange.startDate);
    const jan4 = new Date(Date.UTC(start.getFullYear(), 0, 4));
    const dayOfWeek = jan4.getUTCDay() || 7;
    const firstMonday = new Date(jan4);
    firstMonday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
    const diff = (start - firstMonday) / (7 * 24 * 60 * 60 * 1000);
    const weekNumber = 1 + Math.floor(diff);
    return `Uge ${weekNumber}`;
  } else if (periodType === 'År') {
    return new Date(dateRange.startDate).getFullYear().toString();
  } else if (periodType === 'Interval') {
    const start = new Date(dateRange.startDate).toLocaleDateString('da-DK', {
      timeZone: 'UTC',
    });
    const end = new Date(dateRange.endDate).toLocaleDateString('da-DK', {
      timeZone: 'UTC',
    });
    return `${start} - ${end}`;
  } else {
    // Måned
    return convertToLocalTime(new Date(dateRange.startDate), {
      month: 'long',
      year: 'numeric',
    });
  }
};

// Persist weekMode in localStorage for better UX
const getInitialWeekMode = () => {
  const stored = window.localStorage.getItem('timeregistration-weekMode');
  return stored === null ? false : stored === 'true';
};

export default function TimeRegistrationView() {
  const { subscription, onPaySubscription } = useContext(SubscriptionContext);
  const userInfo = useContext(UserInfoContext);
  const employees = useContext(EmployeesContext);
  const { logs, getFilteredLogs } = useLogs();
  const [activeTab, setActiveTab] = useState('Overblik');
  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [periodType, setPeriodTypeRaw] = useState(getInitialPeriodType);
  const [valueToDisplay, setValueToDisplay] = useState(
    getInitialValueToDisplay(dateRange, getInitialPeriodType())
  );
  const [employeeToDisplayInFilter, setEmployeeToDisplayInFilter] = useState(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [values, setValues] = useState({
    timeLogSettings: userInfo?.companyData?.timeLogSettings,
  });
  const [employeesToDisplay, setEmployeesToDisplay] = useState(employees);
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEmployeeId, setModalEmployeeId] = useState(null);
  const [modalDate, setModalDate] = useState(null);
  const [weekMode, setWeekMode] = useState(getInitialWeekMode());

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.localStorage.setItem('timeregistration-weekMode', weekMode);
  }, [weekMode]);

  useEffect(() => {
    setValueToDisplay(getInitialValueToDisplay(dateRange, periodType));
  }, [dateRange, periodType]);

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const tabSegment = pathSegments[pathSegments.length - 1];
    switch (tabSegment) {
      case 'registreringer':
        setActiveTab('Registreringer');
        break;
      case 'overskridelser':
        setActiveTab('Overskridelser');
        break;
      case 'indstillinger':
        setActiveTab('Indstillinger');
        break;
      case 'overblik':
      default:
        setActiveTab('Overblik');
    }
  }, [location]);

  useEffect(() => {
    if (employees.length > 0) {
      setEmployeesLoading(false);
      // Filter employees based on showInactive and mobileApp.access
      let filtered;
      if (showInactive) {
        // Show only inactive employees with an endDate
        filtered = employees.filter(
          (emp) => emp.data.employmentStatus === 'inactive' && emp.data.endDate
        );
      } else {
        // Show active employees with mobileApp access
        filtered = employees.filter(
          (emp) =>
            emp.data.employmentStatus !== 'inactive' &&
            emp.data.settings?.mobileApp?.access
        );
      }

      setEmployeesToDisplay(filtered);
      // Restore employee filter from localStorage if present
      const storedEmployees = getInitialEmployee(filtered);
      if (storedEmployees && storedEmployees.length > 0) {
        setEmployeeToDisplayInFilter(storedEmployees);
        setEmployeesToDisplay(storedEmployees);
      }
    }
  }, [employees, showInactive]);

  useEffect(() => {
    if (userInfo.companyData) {
      setValues({
        timeLogSettings: userInfo.companyData.timeLogSettings,
      });
    }
  }, [userInfo.companyData]);

  // Persist dateRange, periodType, and employee filter in localStorage
  useEffect(() => {
    window.localStorage.setItem(
      'timeregistration-dateRange',
      JSON.stringify({ value: dateRange, expires: getExpiry() })
    );
  }, [dateRange]);

  useEffect(() => {
    window.localStorage.setItem(
      'timeregistration-periodType',
      JSON.stringify({ value: periodType, expires: getExpiry() })
    );
  }, [periodType]);

  useEffect(() => {
    if (
      Array.isArray(employeeToDisplayInFilter) &&
      employeeToDisplayInFilter.length > 0
    ) {
      window.localStorage.setItem(
        'timeregistration-employee',
        JSON.stringify({
          value: employeeToDisplayInFilter.map((emp) => emp.id || emp.value),
          expires: getExpiry(),
        })
      );
    } else {
      window.localStorage.removeItem('timeregistration-employee');
    }
  }, [employeeToDisplayInFilter]);

  useEffect(() => {
    const fetchAllLogs = async () => {
      setIsLoading(true);
      await Promise.all(
        employees.map((employee) => {
          // For inactive employees, only fetch logs up to their endDate
          if (
            employee.data.employmentStatus === 'inactive' &&
            employee.data.endDate
          ) {
            const endDateISO = employee.data.endDate
              .toDate()
              .toISOString()
              .split('T')[0];
            // If the requested dateRange starts after the endDate, skip this employee
            if (dateRange.startDate > endDateISO) {
              return Promise.resolve();
            }
            // Adjust the end date if needed
            const adjustedEndDate =
              dateRange.endDate > endDateISO ? endDateISO : dateRange.endDate;
            return getFilteredLogs({
              userId: employee.id,
              startDate: dateRange.startDate,
              endDate: adjustedEndDate,
            });
          }
          // For active employees, fetch logs normally
          return getFilteredLogs({ userId: employee.id, ...dateRange });
        })
      );
      setIsLoading(false);
    };
    fetchAllLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Never add fetchAllLogs to dependencies, it will cause an infinite loop
  }, [dateRange, employees]);

  const updateActiveTab = (newTab) => {
    setActiveTab(newTab);
    const basePath = `/tidsregistrering`;
    switch (newTab) {
      case 'Registreringer':
        navigate(`${basePath}/registreringer`, { replace: true });
        break;
      case 'Overskridelser':
        navigate(`${basePath}/overskridelser`, { replace: true });
        break;
      case 'Indstillinger':
        navigate(`${basePath}/indstillinger`, { replace: true });
        break;
      case 'Overblik':
      default:
        navigate(`${basePath}`, { replace: true });
    }
  };

  // Function to open modal for new registration
  const openNewRegistrationModal = () => {
    setModalEmployeeId(null);
    // Always set to today's date for new registrations, regardless of current view mode
    // This ensures we have a valid date even if we're in week mode (which uses formats like "2025-U31")
    setModalDate(new Date().toISOString().split('T')[0]); // Today's date
    setIsModalOpen(true);
  };

  // Filter employees based on showInactive state and exempt status
  const filteredEmployees = employees
    .filter((employee) => {
      // Filter by inactive status based on showInactive state
      if (showInactive) {
        return (
          employee?.data?.employmentStatus === 'inactive' &&
          employee?.data?.endDate
        );
      } else {
        return employee?.data?.employmentStatus !== 'inactive';
      }
    })
    .filter((employee) => !employee?.data?.settings?.timeRegistration?.exempt);

  // filteredEmployeesToDisplay is already declared above, do not redeclare
  const filteredEmployeesToDisplay = filteredEmployees.filter((employee) =>
    employeesToDisplay.some((emp) => emp.id === employee.id)
  );

  if (isLoading || employeesLoading) {
    return <PreLoader />;
  }

  return (
    <div className="main-layout">
      <PageTop
        title={
          activeTab === 'Indstillinger'
            ? `Indstillinger til tidsregistrering`
            : 'Tidsregistrering'
        }
        actions={
          (onPaySubscription?.onPayData || subscription?.trial) && (
            <>
              <Button
                buttonStyle="secondary"
                onClick={openNewRegistrationModal}
              >
                Ny registrering
              </Button>
              <Button
                buttonStyle="secondary"
                href="/tidsregistrering/indstillinger"
              >
                Indstillinger
              </Button>
            </>
          )
        }
      />
      {((subscription.status === 'active' && onPaySubscription?.onPayData) ||
        subscription.trial) && (
        <Tabs
          tabs={[
            { label: 'Overblik' },
            { label: 'Registreringer' },
            { label: 'Overskridelser' },
          ]}
          activeTab={activeTab}
          setActiveTab={updateActiveTab}
        />
      )}
      {(activeTab !== 'Indstillinger' &&
        !showInactive &&
        filteredEmployees.length === 0) ||
      subscription.status === 'expired' ? (
        <EmptyState image={EmptyStateImage}>
          {!onPaySubscription?.onPayData && !subscription?.trial && (
            <span className="equipment-limited-access">
              Kræver tilknyttet betalingskort
            </span>
          )}
          <h3>Tidsregistrering som IKKE stjæler medarbejdernes tid</h3>
          <ul
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              fontSize: '1rem',
              marginBottom: '2rem',
            }}
          >
            <li>Opsæt og onboard medarbejdere i 4 hurtige steps</li>
            <li>Registrér arbejdstid og fravær med få klik i app</li>
            <li>Overhold alle lovkrav og 48-timers reglen</li>
            <li>Dokumentation og opbevaring af data i minimum fem år (krav)</li>
          </ul>
          {onPaySubscription?.onPayData || subscription?.trial ? (
            <Button
              buttonStyle="primary"
              href="/tidsregistrering/indstillinger"
            >
              Kom godt igang
            </Button>
          ) : (
            <>
              <p>
                For at kunne bruge tidsregistrering, kræver det et betalingskort
                tilknyttet.
              </p>
              <Button buttonStyle="primary" href="/subscription">
                Tilknyt betalingskort
              </Button>
            </>
          )}
        </EmptyState>
      ) : (
        <>
          {activeTab === 'Overblik' && (
            <TimeRegistrationOverview
              logs={logs}
              employees={filteredEmployees}
              employeesToDisplay={filteredEmployeesToDisplay}
              setEmployeesToDisplay={setEmployeesToDisplay}
              dateRange={dateRange}
              setDateRange={setDateRange}
              companyData={userInfo.companyData}
              valueToDisplay={valueToDisplay}
              setValueToDisplay={setValueToDisplay}
              employeeToDisplayInFilter={employeeToDisplayInFilter}
              setEmployeeToDisplayInFilter={setEmployeeToDisplayInFilter}
              periodType={periodType}
              setPeriodType={setPeriodTypeRaw}
              showInactive={showInactive}
              setShowInactive={setShowInactive}
            />
          )}
          {activeTab === 'Registreringer' && (
            <TimeRegistrationRegistrations
              employees={filteredEmployees}
              employeesToDisplay={filteredEmployeesToDisplay}
              setEmployeesToDisplay={setEmployeesToDisplay}
              dateRange={dateRange}
              setDateRange={setDateRange}
              companyData={userInfo.companyData}
              valueToDisplay={valueToDisplay}
              setValueToDisplay={setValueToDisplay}
              employeeToDisplayInFilter={employeeToDisplayInFilter}
              setEmployeeToDisplayInFilter={setEmployeeToDisplayInFilter}
              weekMode={weekMode}
              setWeekMode={setWeekMode}
              periodType={periodType}
              setPeriodType={setPeriodTypeRaw}
              showInactive={showInactive}
              setShowInactive={setShowInactive}
              modalDate={modalDate}
              modalEmployeeId={modalEmployeeId}
              setModalEmployeeId={setModalEmployeeId}
              setModalDate={setModalDate}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
            />
          )}
          {activeTab === 'Overskridelser' && (
            <TimeRegistrationExceedings employees={filteredEmployees} />
          )}
        </>
      )}
      {activeTab === 'Indstillinger' && (
        <TimeRegistrationSettings
          userInfo={userInfo}
          initialValues={values}
          setInitialValues={setValues}
        />
      )}
      <TimeRegistrationUserRegistrationsModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        modalEmployeeId={modalEmployeeId}
        setModalEmployeeId={setModalEmployeeId}
        modalDate={modalDate}
        setModalDate={setModalDate}
      />
    </div>
  );
}
